import {MediaRepo} from "../api/media.repo";
import {inject, Injectable} from "@angular/core";
import {DuckDbHelper, sqlDirection, sqlLike, sqlNumber, sqlText} from "./duckdb.helper";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {Observable} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {map} from "rxjs/operators";
import {isDefined, nonBlank} from "../../libs/utils/epic.utils";
import {LoggingService} from "../../services/logger/logging.service";
import {SearchMediaRes} from "../dto/search/search-media.res";
import {SearchFilterResponse} from "../dto/search/search-table.dto.res";
import {HttpResponse} from "@angular/common/http";
import {DUCKDB_TRANSPORT, DuckDbTransport} from "./duckdb-transport";

@Injectable({providedIn: 'root'})
export class MediaDuckDb extends MediaRepo {

  private readonly duck: DuckDbHelper = inject(DuckDbHelper);
  private readonly log: LoggingService = inject(LoggingService);
  private readonly transport: DuckDbTransport | null = inject(DUCKDB_TRANSPORT, {optional: true});

  // Electron liefert den Inhalt ueber seinen Webserver aus, also bleibt dort der geerbte HTTP-Abruf.
  public override loadContent(org: OrgDto, mediaId: string): Observable<HttpResponse<Blob>> {
    if (isDefined(this.transport)) {
      return this.transport.loadMedia(mediaId);
    }
    return super.loadContent(org, mediaId);
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchMediaRes>> {
    const limit = request.pageSize;
    const offset = request.pageSize * (request.page - 1);
    const sort = this.buildSort(request);
    const query = this.buildQuery(request);
    const filter = this.buildFilter(request);

    // `m.content_error` wird bewusst nicht gelesen: Die Spalte gibt es erst in neueren Exporten, und ein
    // Verweis darauf laesst die Abfrage gegen jeden aelteren Export scheitern - fuer ein Feld, das kein
    // DTO auswertet. Der Export schreibt sie weiter; wer sie anzeigen will, braucht zuerst ein Feld im DTO.
    const search = `
      SELECT m.id           AS id,
             m.type         AS type,
             m.filename     AS filename,
             m.size         AS "size",
             m.created      AS created,
             m.content_type AS contentType,
             m.attachment   AS attachment,
             m.contact_id   AS contactId,
             m.address_id   AS addressId,
             m.vehicle_id   AS vehicleId,
             m.invoice_id   AS invoiceId,
             m.article_id   AS articleId,
             m.receipt_id   AS receiptId,
             COUNT(*)          OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING)               AS total_results
      FROM media m
      WHERE ${isDefined(query) ? query : '1=1'}
        AND ${isDefined(filter) ? filter : '1=1'}
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res) => {
      const hasRes = isDefined(res) && res.length > 0;
      const firstRow = hasRes ? res[0] : {total_results: 0} as any;
      const result = new SearchMediaRes({
        items: hasRes ? res : [],
        hits: firstRow.total_results,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters.map((f) => new SearchFilterResponse(f))
      });
      return DuckDbHelper.data('SearchMediaRes', result);
    }));
  }

  private buildSort(request: SearchRequest): string | undefined {
    let field;
    if (isDefined(request.sortField)) {
      switch (request.sortField) {
        case 'created':
          field = 'm.created';
          break;
        case 'filename':
          field = 'm.filename';
          break;
        case 'size':
          field = 'm.size';
          break;
        default:
          this.log.warn('Unhandled sort field "{}"', [request.sortField]);
          field = 'm.created';
      }
    } else {
      field = 'm.created';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildQuery(request: SearchRequest): string | undefined {
    if (isDefined(request.query) && nonBlank(request.query)) {
      return `(m.filename ILIKE ${sqlLike(request.query)})`;
    }
    return undefined;
  }

  private buildFilter(request: SearchRequest): string | undefined {
    const filters: string[] = [];
    const contactFilter = request.filters.find((f: any) => f.id === 'contactId');
    if (isDefined(contactFilter) && isDefined(contactFilter.selected) && contactFilter.selected.length === 1) {
      filters.push(`m.contact_id = ${sqlText(contactFilter.selected[0])}`);
    }
    const vehicleFilter = request.filters.find((f: any) => f.id === 'vehicleId');
    if (isDefined(vehicleFilter) && isDefined(vehicleFilter.selected) && vehicleFilter.selected.length === 1) {
      filters.push(`m.vehicle_id = ${sqlText(vehicleFilter.selected[0])}`);
    }
    // Der Export fuehrt keine Sterbefaelle und deshalb keine Spalte death_id. Den Filter ersatzlos zu
    // streichen waere schlimmer als ein Fehler: Der Dokumente-Reiter eines Falls listete dann SAEMTLICHE
    // Medien des Exports - die Unterlagen fremder Familien am eigenen Fall, ohne jedes Anzeichen.
    const deathFilter = request.filters.find((f: any) => f.id === 'deathId');
    if (isDefined(deathFilter) && isDefined(deathFilter.selected) && deathFilter.selected.length === 1) {
      filters.push('1=0');
    }
    const invoiceFilter = request.filters.find((f: any) => f.id === 'invoiceId');
    if (isDefined(invoiceFilter) && isDefined(invoiceFilter.selected) && invoiceFilter.selected.length === 1) {
      filters.push(`m.invoice_id = ${sqlText(invoiceFilter.selected[0])}`);
    }
    const articleFilter = request.filters.find((f: any) => f.id === 'articleId');
    if (isDefined(articleFilter) && isDefined(articleFilter.selected) && articleFilter.selected.length === 1) {
      filters.push(`m.article_id = ${sqlText(articleFilter.selected[0])}`);
    }
    const receiptFilter = request.filters.find((f: any) => f.id === 'receiptId');
    if (isDefined(receiptFilter) && isDefined(receiptFilter.selected) && receiptFilter.selected.length === 1) {
      filters.push(`m.receipt_id = ${sqlText(receiptFilter.selected[0])}`);
    }
    // MediaListComponent sendet `sheetId`, der Export kennt aber weder eine Tabelle `sheet` noch eine
    // Spalte `media.sheet_id`. Ohne diesen Zweig bliebe die Filterliste leer und der Dokumente-Reiter
    // eines Blattes listete SAEMTLICHE Medien des Exports - dieselbe Falle wie beim Sterbefall oben.
    const sheetFilter = request.filters.find((f: any) => f.id === 'sheetId');
    if (isDefined(sheetFilter) && isDefined(sheetFilter.selected) && sheetFilter.selected.length === 1) {
      filters.push('1=0');
    }
    return filters.length > 0 ? filters.join(' AND ') : undefined;
  }
}
